import { Extension, Mark, mergeAttributes } from '@tiptap/react';
import { Plugin, PluginKey, TextSelection, type EditorState } from '@tiptap/pm/state';
import { ReplaceStep, AddMarkStep, RemoveMarkStep } from '@tiptap/pm/transform';
import type { Node as PMNode } from '@tiptap/pm/model';
import { Fragment, Slice } from '@tiptap/pm/model';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { isLiveDocumentTransaction } from './document-live-editing';

const key = new PluginKey('documentInlineReview');
const tracked = (name: string) => name === 'trackedInsertion' || name === 'trackedDeletion';
function reviewMark(name: 'trackedInsertion' | 'trackedDeletion') {
  return Mark.create({
    name, inclusive: false, excludes: 'trackedInsertion trackedDeletion',
    addAttributes: () => ({ changeId: { default: null } }),
    // Never trust pasted HTML attribution or operation IDs.
    parseHTML: () => [],
    renderHTML: ({ HTMLAttributes }) => [name === 'trackedInsertion' ? 'ins' : 'del', mergeAttributes({
      class: name === 'trackedInsertion' ? 'underline decoration-2 underline-offset-4' : 'line-through opacity-60',
      'data-inline-review': name
    }, HTMLAttributes), 0]
  });
}
export const TrackedInsertion = reviewMark('trackedInsertion');
export const TrackedDeletion = reviewMark('trackedDeletion');

export type InlineChangeKind = 'insertion' | 'deletion';
export type InlineChangeTarget = { id: string; kind: InlineChangeKind };
export type InlineChangeRange = { id: string; from: number; to: number };
function changeIdOf(attrs: Record<string, unknown>) {
  return typeof attrs.changeId === 'string' && attrs.changeId ? attrs.changeId : undefined;
}
/** The tracked inline insertion or deletion touching a document position, if any. */
export function inlineChangeAt(state: EditorState, position: number): InlineChangeTarget | null {
  const resolved = state.doc.resolve(Math.max(0, Math.min(position, state.doc.content.size)));
  for (const mark of [...(resolved.nodeAfter?.marks ?? []), ...(resolved.nodeBefore?.marks ?? [])]) {
    const id = tracked(mark.type.name) ? changeIdOf(mark.attrs) : undefined;
    if (id) return { id, kind: mark.type.name === 'trackedInsertion' ? 'insertion' : 'deletion' };
  }
  return null;
}
/** One range per pending inline change (insertion and deletion share an ID), in document order. */
export function inlineChangeRanges(doc: PMNode): InlineChangeRange[] {
  const ranges = new Map<string, InlineChangeRange>();
  doc.descendants((node, position) => {
    if (!node.isText) return;
    for (const mark of node.marks) {
      const id = tracked(mark.type.name) ? changeIdOf(mark.attrs) : undefined;
      if (!id) continue;
      const end = position + node.nodeSize, current = ranges.get(id);
      ranges.set(id, current ? { id, from: Math.min(current.from, position), to: Math.max(current.to, end) } : { id, from: position, to: end });
    }
  });
  return [...ranges.values()].sort((a, b) => a.from - b.from);
}
/**
 * The next or previous inline change from the current selection, wrapping around the document.
 * A change that starts at the caret counts as next unless it is already the selected change.
 */
export function adjacentInlineChange(ranges: InlineChangeRange[], selection: { from: number; to: number }, direction: 1 | -1): { range: InlineChangeRange; index: number } | undefined {
  if (!ranges.length) return undefined;
  const { from, to } = selection;
  let index = direction === 1 ? ranges.findIndex(range => range.from > from || (range.from === from && range.to !== to)) : -1;
  if (direction === -1) ranges.forEach((range, at) => { if (range.from < from) index = at; });
  if (index < 0) index = direction === 1 ? 0 : ranges.length - 1;
  return { range: ranges[index]!, index };
}

function marked(doc: PMNode, from: number, to: number) {
  let found = false;
  doc.nodesBetween(from, to, node => { if (node.marks.some(mark => tracked(mark.type.name))) found = true; });
  return found;
}
export const DocumentInlineTracking = Extension.create<{ enabled: () => boolean; onBlocked: (message: string) => void }>({
  name: 'documentInlineTracking',
  addOptions: () => ({ enabled: () => false, onBlocked: () => {} }),
  addProseMirrorPlugins() {
    const options = this.options;
    return [new Plugin({
      key,
      filterTransaction(transaction, state) {
        // Reviewed Document agent edits build their own tracked marks after checking for pending ones.
        if (!transaction.docChanged || isLiveDocumentTransaction(transaction) || transaction.getMeta('documentReplacement') || transaction.getMeta('documentAgentEdit') || transaction.getMeta(key) || transaction.getMeta('history$')) return true;
        // Pending tracked ranges require a decision first, including when tracking is off.
        for (let index = 0; index < transaction.steps.length; index++) {
          const step = transaction.steps[index];
          if ((step instanceof ReplaceStep || step instanceof AddMarkStep || step instanceof RemoveMarkStep) && step.from !== step.to && marked(transaction.docs[index]!, step.from, step.to)) {
            options.onBlocked('Accept or reject the pending inline edit before changing its text.'); return false;
          }
        }
        if (!options.enabled()) return true;
        if (transaction.steps.length !== 1 || !(transaction.steps[0] instanceof ReplaceStep)) return true; // formatting/structural edits retain native saved-group review
        const step = transaction.steps[0], from = state.doc.resolve(step.from), to = state.doc.resolve(step.to);
        if (!from.sameParent(to) || !from.parent.isTextblock || !step.slice.content.size && step.from === step.to) return true;
        let supported = true;
        step.slice.content.forEach(node => { if (!node.isText || node.marks.some(mark => tracked(mark.type.name))) supported = false; });
        if (!supported) return true;
        return true;
      },
      appendTransaction(transactions, oldState, newState) {
        if (!options.enabled() || transactions.some(tr => isLiveDocumentTransaction(tr) || tr.getMeta(key) || tr.getMeta('documentReplacement') || tr.getMeta('documentAgentEdit') || tr.getMeta('history$'))) return null;
        const changes = transactions.filter(tr => tr.docChanged);
        if (changes.length !== 1 || changes[0]!.steps.length !== 1) return null;
        const source = changes[0]!, step = source.steps[0];
        if (!(step instanceof ReplaceStep)) return null;
        const from = oldState.doc.resolve(step.from), to = oldState.doc.resolve(step.to);
        if (!from.sameParent(to) || !from.parent.isTextblock) return null;
        let supported = true;
        step.slice.content.forEach(node => { if (!node.isText || node.marks.some(mark => tracked(mark.type.name))) supported = false; });
        const removed = oldState.doc.slice(step.from, step.to);
        removed.content.forEach(node => { if (!node.isText) supported = false; });
        if (!supported || (!removed.size && !step.slice.size)) return null;
        const changeId = crypto.randomUUID(), tr = newState.tr, insertion = newState.schema.marks.trackedInsertion!, deletion = newState.schema.marks.trackedDeletion!;
        const original = removed.content.content.map(node => node.mark([...node.marks.filter(mark => !tracked(mark.type.name)), deletion.create({ changeId })]));
        if (original.length) tr.insert(step.from, new Slice(Fragment.fromArray(original), 0, 0).content);
        const insertedFrom = step.from + removed.size, insertedTo = insertedFrom + step.slice.size;
        if (insertedTo > insertedFrom) tr.addMark(insertedFrom, insertedTo, insertion.create({ changeId }));
        tr.setSelection(TextSelection.create(tr.doc, insertedTo)).setStoredMarks(null);
        return tr.setMeta(key, true);
      }
    })];
  }
});

export type CommentRange = { id: string; from: number; to: number };
export const DocumentCommentHighlights = Extension.create<{ ranges: () => CommentRange[]; active: () => string | undefined }>({
  name: 'documentCommentHighlights',
  addOptions: () => ({ ranges: () => [], active: () => undefined }),
  addProseMirrorPlugins() {
    const options = this.options;
    const commentKey = new PluginKey<DecorationSet>('documentCommentRanges');
    return [new Plugin<DecorationSet>({
      key: commentKey,
      state: {
        init: () => DecorationSet.empty,
        apply(transaction, previous) {
          if (transaction.getMeta('documentCommentRanges')) return DecorationSet.create(transaction.doc, options.ranges().filter(range => range.from >= 0 && range.to <= transaction.doc.content.size && range.to > range.from).map(range => Decoration.inline(range.from, range.to, {
            class: options.active() === range.id ? 'bg-foreground/20 border-b-2 border-foreground' : 'bg-muted border-b border-foreground/40',
            'data-comment-id': range.id
          })));
          if (transaction.getMeta('documentReplacement')) return DecorationSet.empty;
          return previous.map(transaction.mapping, transaction.doc);
        }
      },
      props: { decorations: state => commentKey.getState(state) ?? DecorationSet.empty }
    })];
  }
});
